/**
 * API Error Handling Utilities
 * Centralized error handling for API responses and network requests
 */

import { notification } from 'antd';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface ApiErrorResponse {
  success: false;
  message: string;
  code?: string;
  details?: any;
  statusCode?: number;
  timestamp?: string;
  path?: string;
}

export interface NetworkError {
  name: 'NetworkError';
  message: string;
  isNetworkError: true;
  originalError: Error;
}

export interface ValidationError {
  name: 'ValidationError';
  message: string;
  field?: string;
  code?: string;
  details?: Record<string, string[]>;
}

export interface AuthenticationError {
  name: 'AuthenticationError';
  message: string;
  code?: 'INVALID_TOKEN' | 'TOKEN_EXPIRED' | 'UNAUTHORIZED';
  requiresLogin?: boolean;
}

export interface TradingError {
  name: 'TradingError';
  message: string;
  code?: 'INSUFFICIENT_FUNDS' | 'MARKET_CLOSED' | 'INVALID_ORDER' | 'POSITION_LIMIT';
  tradingSpecific: true;
}

export type AppError =
  | NetworkError
  | ValidationError
  | AuthenticationError
  | TradingError
  | Error;

export interface ErrorHandlingOptions {
  showToast?: boolean;
  toastType?: 'error' | 'warning' | 'info';
  logError?: boolean;
  reportError?: boolean;
  retryable?: boolean;
  customMessage?: string;
  context?: string;
}

// =============================================================================
// ERROR CLASSIFICATION
// =============================================================================

export const isNetworkError = (error: any): error is NetworkError => {
  return error?.name === 'NetworkError' ||
    error?.isNetworkError === true ||
    error?.code === 'NETWORK_ERROR' ||
    !navigator.onLine;
};

export const isValidationError = (error: any): error is ValidationError => {
  return error?.name === 'ValidationError' ||
    error?.statusCode === 422 ||
    error?.code === 'VALIDATION_ERROR';
};

export const isAuthenticationError = (error: any): error is AuthenticationError => {
  return error?.name === 'AuthenticationError' ||
    error?.statusCode === 401 ||
    error?.code === 'UNAUTHORIZED' ||
    error?.code === 'INVALID_TOKEN' ||
    error?.code === 'TOKEN_EXPIRED';
};

export const isTradingError = (error: any): error is TradingError => {
  return error?.name === 'TradingError' ||
    error?.tradingSpecific === true ||
    ['INSUFFICIENT_FUNDS', 'MARKET_CLOSED', 'INVALID_ORDER', 'POSITION_LIMIT']
      .includes(error?.code);
};

export const isRetryableError = (error: any): boolean => {
  // Network errors are generally retryable
  if (isNetworkError(error)) return true;

  // 5xx server errors are retryable
  if (error?.statusCode >= 500 && error?.statusCode < 600) return true;

  // Rate limiting is retryable with backoff
  if (error?.statusCode === 429) return true;

  // Timeout errors are retryable
  if (error?.code === 'TIMEOUT' || error?.name === 'TimeoutError') return true;

  return false;
};

// =============================================================================
// ERROR TRANSFORMATION
// =============================================================================

export const transformApiError = (error: any): AppError => {
  // Handle network errors
  if (error?.name === 'TypeError' && error?.message?.includes('fetch')) {
    return {
      name: 'NetworkError',
      message: 'Network connection failed. Please check your internet connection.',
      isNetworkError: true,
      originalError: error,
    } as NetworkError;
  }

  // Handle HTTP response errors
  if (error?.response) {
    const { status, data } = error.response;
    const message = data?.message || data?.error || 'An error occurred';

    // Authentication errors
    if (status === 401) {
      return {
        name: 'AuthenticationError',
        message: message || 'Authentication required',
        code: data?.code || 'UNAUTHORIZED',
        requiresLogin: true,
      } as AuthenticationError;
    }

    // Validation errors
    if (status === 422) {
      return {
        name: 'ValidationError',
        message: message || 'Validation failed',
        details: data?.details || data?.errors,
      } as ValidationError;
    }

    // Trading-specific errors
    if (data?.tradingError || isTradingError(data)) {
      return {
        name: 'TradingError',
        message: message,
        code: data?.code,
        tradingSpecific: true,
      } as TradingError;
    }

    // Generic API error
    return new Error(`${message} (${status})`);
  }

  // Return original error if not transformable
  return error instanceof Error ? error : new Error(String(error));
};

// =============================================================================
// USER-FRIENDLY ERROR MESSAGES
// =============================================================================

export const getUserFriendlyMessage = (error: AppError): string => {
  if (isNetworkError(error)) {
    return 'Connection problem. Please check your internet connection and try again.';
  }

  if (isAuthenticationError(error)) {
    if (error.code === 'TOKEN_EXPIRED') {
      return 'Your session has expired. Please log in again.';
    }
    return 'Authentication required. Please log in to continue.';
  }

  if (isValidationError(error)) {
    return error.details
      ? 'Please check the form and correct any errors.'
      : 'Invalid input. Please check your data and try again.';
  }

  if (isTradingError(error)) {
    switch (error.code) {
      case 'INSUFFICIENT_FUNDS':
        return 'Insufficient funds to complete this trade.';
      case 'MARKET_CLOSED':
        return 'Markets are currently closed. Please try again during trading hours.';
      case 'INVALID_ORDER':
        return 'Invalid order parameters. Please check your order details.';
      case 'POSITION_LIMIT':
        return 'Position limit exceeded. Please reduce your position size.';
      default:
        return (error as any).message || 'Trading error occurred. Please try again.';
    }
  }

  // Generic error message
  return error.message || 'An unexpected error occurred. Please try again.';
};

// =============================================================================
// ERROR LOGGING
// =============================================================================

export const logError = (
  error: AppError,
  context?: string,
  additionalData?: Record<string, any>
) => {
  // Handle empty or malformed error objects
  if (!error) {
    console.error('logError called with null/undefined error');
    return;
  }

  // Debug what we received
  console.debug('logError received:', { error, context, additionalData });

  // Force refresh timestamp
  const refreshId = Date.now();

  // Safely extract error properties
  const safeError = error as any;

  try {
    const errorLog = {
      timestamp: new Date().toISOString(),
      error: {
        name: safeError?.name || 'Unknown Error',
        message: safeError?.message || 'No error message provided',
        stack: safeError?.stack || 'No stack trace available',
        status: safeError?.status || safeError?.statusCode || 'Unknown status',
        code: safeError?.code || 'Unknown code',
      },
      context: context || 'No context provided',
      additionalData: additionalData || {},
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
      userId: typeof localStorage !== 'undefined' ? localStorage.getItem('userId') : null,
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      // Temporarily use warn instead of error to avoid console error handler
      console.warn('Error Log:', errorLog);
    }
  } catch (logErr) {
    console.error('Failed to construct error log:', logErr);
    console.error('Original error data:', { error, context, additionalData });
  }

  // Send to logging service in production
  if (process.env.NODE_ENV === 'production') {
    // TODO: Implement external logging service
    // fetch('/api/errors', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(errorLog),
    // }).catch(console.error);
  }
};

// =============================================================================
// MAIN ERROR HANDLER
// =============================================================================

export const handleApiError = (
  error: any,
  options: ErrorHandlingOptions = {}
): AppError => {
  const {
    showToast = true,
    toastType = 'error',
    logError: shouldLog = true,
    customMessage,
    context = 'API Request',
  } = options;

  // Transform error
  const transformedError = transformApiError(error);

  // Log error if enabled
  if (shouldLog) {
    logError(transformedError, context, { originalError: error });
  }

  // Show notification if enabled
  if (showToast) {
    const message = customMessage || getUserFriendlyMessage(transformedError);

    switch (toastType) {
      case 'error':
        notification.error({
          message: 'Error',
          description: message,
          duration: 4.5,
        });
        break;
      case 'warning':
        notification.warning({
          message: 'Warning',
          description: message,
          duration: 4.5,
        });
        break;
      case 'info':
        notification.info({
          message: 'Information',
          description: message,
          duration: 4.5,
        });
        break;
    }
  }

  return transformedError;
};

// =============================================================================
// RETRY UTILITIES
// =============================================================================

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  retryCondition?: (error: AppError) => boolean;
}

export const withRetry = async <T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    retryCondition = isRetryableError,
  } = options;

  let lastError: AppError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = transformApiError(error);

      // Don't retry if this is the last attempt or error is not retryable
      if (attempt === maxRetries || !retryCondition(lastError)) {
        throw lastError;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(baseDelay * Math.pow(backoffFactor, attempt), maxDelay);

      // Add jitter to prevent thundering herd
      const jitter = Math.random() * 0.1 * delay;
      const totalDelay = delay + jitter;

      console.log(`Retrying operation in ${Math.round(totalDelay)}ms (attempt ${attempt + 1}/${maxRetries + 1})`);

      await new Promise(resolve => setTimeout(resolve, totalDelay));
    }
  }

  throw lastError!;
};

// =============================================================================
// REACT QUERY ERROR HANDLING
// =============================================================================

export const createQueryErrorHandler = (context: string) => (error: any) => {
  handleApiError(error, {
    context: `React Query - ${context}`,
    showToast: true,
    logError: true,
  });
};

export const createMutationErrorHandler = (context: string) => (error: any) => {
  return handleApiError(error, {
    context: `React Query Mutation - ${context}`,
    showToast: true,
    logError: true,
  });
};
